import React, { useState, useEffect } from 'react';
import { Box, Flex, Heading, Text, Container, Button, useClipboard, Tooltip, Icon, Stack, Link } from '@chakra-ui/react';
import { PRESALE_WALLET_ADDRESS, NETWORK } from '../utils/constants';
import { getAddressUrl } from '../utils/formatters';

// External link icon
const ExternalLinkIcon = (props) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M18 13V19C18 19.5304 17.7893 20.0391 17.4142 20.4142C17.0391 20.7893 16.5304 21 16 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V8C3 7.46957 3.21071 6.96086 3.58579 6.58579C3.96086 6.21071 4.46957 6 5 6H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M15 3H21V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M10 14L21 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Copy icon
const CopyIcon = (props) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M20 9H11C9.89543 9 9 9.89543 9 11V20C9 21.1046 9.89543 22 11 22H20C21.1046 22 22 21.1046 22 20V11C22 9.89543 21.1046 9 20 9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M5 15H4C3.46957 15 2.96086 14.7893 2.58579 14.4142C2.21071 14.0391 2 13.5304 2 13V4C2 3.46957 2.21071 2.96086 2.58579 2.58579C2.96086 2.21071 3.46957 2 4 2H13C13.5304 2 14.0391 2.21071 14.4142 2.58579C14.7893 2.96086 15 3.46957 15 4V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PresaleStatus = ({ onStatusChange }) => {
  const [timeLeft, setTimeLeft] = useState({});
  const [status, setStatus] = useState('pending');
  
  useEffect(() => {
    const startDate = new Date('2025-02-24T11:00:00');
    const endDate = new Date(startDate.getTime() + (35 * 60 * 60 * 1000)); // 36 hours after start
    
    const calculateTimeLeft = () => {
      const now = new Date();
      let difference;
      let currentStatus;
      
      if (now < startDate) {
        difference = startDate - now;
        currentStatus = 'pending';
      } else if (now < endDate) {
        difference = endDate - now;
        currentStatus = 'active';
      } else {
        currentStatus = 'ended';
        return null;
      }
      
      return {
        status: currentStatus,
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60)
      };
    };
    
    const timer = setInterval(() => {
      const timeLeft = calculateTimeLeft();
      if (timeLeft) {
        setTimeLeft(timeLeft);
        setStatus(timeLeft.status);
        onStatusChange(timeLeft.status);
      } else {
        clearInterval(timer);
        setStatus('ended');
        onStatusChange('ended');
      }
    }, 1000);
    
    return () => clearInterval(timer);
  }, [onStatusChange]);
  
  if (!timeLeft) return null;
  
  if (status === 'pending') {
    return (
      <Box 
        bg="yakuza.gold" 
        color="yakuza.black" 
        px={4} 
        py={2} 
        borderRadius="md"
        fontWeight="bold"
        textAlign="center"
      >
        Starts in: {String(timeLeft.days).padStart(2, '0')}d {String(timeLeft.hours).padStart(2, '0')}h {String(timeLeft.minutes).padStart(2, '0')}m {String(timeLeft.seconds).padStart(2, '0')}s
      </Box>
    );
  }
  
  if (status === 'active') {
    return (
      <Box 
        bg="yakuza.red" 
        color="white" 
        px={4} 
        py={2} 
        borderRadius="md"
        fontWeight="bold"
        textAlign="center"
      >
        Ends in: {String(timeLeft.days).padStart(2, '0')}d {String(timeLeft.hours).padStart(2, '0')}h {String(timeLeft.minutes).padStart(2, '0')}m {String(timeLeft.seconds).padStart(2, '0')}s
      </Box>
    );
  }
  
  
  return (
    <Box 
      bg="gray.500" 
      color="white" 
      px={4} 
      py={2} 
      borderRadius="md"
      fontWeight="bold"
      textAlign="center"
    >
      Presale Ended
    </Box>
  );
};

const Header = () => {
  const { hasCopied, onCopy } = useClipboard(PRESALE_WALLET_ADDRESS);
  const [presaleStatus, setPresaleStatus] = useState('pending');
  
  return (
    <Box 
      as="header" 
      bg="yakuza.black" 
      borderBottom="4px solid"
      borderColor="yakuza.red"
      py={4}
      boxShadow="0 4px 8px rgba(0, 0, 0, 0.5)"
    >
      <Container maxW="1200px">
        <Flex direction={{ base: 'column', lg: 'row' }} align="center" justify="space-between" gap={4}>
          <Flex align="center">
            <Link href="https://www.yakuzabossbase.com" isExternal>
              <Box as="img" src="/ylogo.png" alt="Yakuza Token" h="60px" mr={4} />
            </Link>
            <Box>
              <Heading as="h1" size="xl" color="yakuza.white" fontWeight="900">
                YAKUZA TOKEN
              </Heading>
              <Text color="yakuza.gold" fontSize="md" mt={1}>
                Presale Dashboard | {NETWORK.name} Network
              </Text>
            </Box>
          </Flex>
          
          <Stack 
            direction="column" 
            spacing={2}
            align={{ base: "center", lg: "flex-end" }}
            mt={{ base: 2, lg: 0 }}
            w={{ base: "100%", lg: "auto" }}
          >
            <PresaleStatus onStatusChange={setPresaleStatus} />
            
            {presaleStatus === 'active' && (
              <Box
                p={3}
                bg="rgba(0,0,0,0.3)"
                borderRadius="md"
                borderWidth="1px"
                borderColor="yakuza.gold"
                w="100%"
              >
                <Text color="yakuza.white" fontSize="sm" fontWeight="bold" mb={1}>
                  Contribution Wallet Address:
                </Text>
                
                <Flex 
                  align="center" 
                  bg="blackAlpha.600" 
                  p={2} 
                  borderRadius="md"
                  fontFamily="mono"
                  flexWrap="wrap"
                  gap={2}
                >
                  <Text 
                    color="yakuza.gold" 
                    fontSize={{ base: "xs", sm: "sm" }}
                    wordBreak="break-all"
                    flex="1"
                  >
                    {PRESALE_WALLET_ADDRESS}
                  </Text>
                  
                  <Flex flexShrink={0}>
                    <Tooltip hasArrow label="Copy address" placement="top">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        colorScheme="red" 
                        onClick={onCopy}
                        mr={1}
                      >
                        <Icon as={CopyIcon} />
                        {hasCopied && <Text fontSize="xs" ml={1}>Copied!</Text>}
                      </Button>
                    </Tooltip>
                    
                    <Tooltip hasArrow label="View on explorer" placement="top">
                      <Button
                        as={Link}
                        href={getAddressUrl(PRESALE_WALLET_ADDRESS)}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                      >
                        <Icon as={ExternalLinkIcon} />
                      </Button>
                    </Tooltip>
                  </Flex>
                </Flex>
              </Box>
            )}
          </Stack>
        </Flex>
      </Container>
    </Box>
  );
};

export default Header;